/**
 * ClientSelector.tsx
 * 
 * Componente per la selezione ibrida dei clienti nell'applicazione Ufficio Stile.
 * Supporta sia la ricerca in dati esterni che la creazione di nuovi clienti inline.
 * Implementa il pattern "Approccio Ibrido" per gestire clienti esistenti e nuovi.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Select,
  TextInput,
  Group,
  Text,
  Badge,
  Loader,
  Box,
  Button,
  ActionIcon
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch, IconPlus, IconBuilding, IconX } from '@tabler/icons-react';
import { useExternalClients, useSearchExternalClients } from '../../../hooks';

interface ClientSelectorProps {
  value?: {
    clientId?: string;
    clientName: string;
  };
  onChange: (client: { clientId?: string; clientName: string }) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

interface ClientOption {
  value: string;
  label: string;
  group: string;
  clientId?: string;
  isExternal?: boolean;
  isNew?: boolean;
}

export function ClientSelector({
  value,
  onChange,
  error,
  disabled = false,
  required = false
}: ClientSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newClientName, setNewClientName] = useState('');

  // Hook per ricerca clienti esterni
  const { data: externalClientsRaw, isLoading: isSearchingExternal, error: searchError } = useSearchExternalClients(
    debouncedSearchQuery
  );
  // fallback a [] se undefined
  const externalClients = externalClientsRaw && Array.isArray(externalClientsRaw) ? externalClientsRaw : [];

  // Opzioni per il Select: solo clienti trovati, con identificatore unico e non vuoto (semplificato, senza useMemo)
  const options: ClientOption[] = Array.isArray(externalClients)
    ? (
        externalClients
          .map(client => {
            const uniqueId = client.id || client.externalId || client.code;
            const label = client?.name?.trim() || client.companyName?.trim() || client.code?.trim() || `Cliente`;
            if (!uniqueId || !label) return null;
            return {
              value: `external-${uniqueId}`,
              label,
              group: 'Clienti Esistenti',
              clientId: String(uniqueId), // always a string here
              isExternal: true
            } as ClientOption;
          })
          .filter((opt): opt is ClientOption => opt !== null)
      )
    : [];

  // Handler per selezione
  const handleSelect = (selectedValue: string | null) => {
    if (!selectedValue) {
      onChange({ clientId: undefined, clientName: searchQuery });
      return;
    }
    const option = options?.find(opt => opt.value === selectedValue);
    if (option && option.isExternal && option.clientId && option.label) {
      onChange({ clientId: option.clientId, clientName: option.label });
      setSearchQuery('');
    } else {
      // fallback difensivo: non dovrebbe mai accadere
      onChange({ clientId: undefined, clientName: searchQuery });
    }
  };

  // Handler per creazione manuale nuovo cliente
  const handleCreateNew = () => {
    if (newClientName.trim()) {
      onChange({ clientId: undefined, clientName: newClientName.trim() });
      setNewClientName('');
      setIsCreatingNew(false);
      setSearchQuery('');
    }
  };

  // Reset stato quando value cambia esternamente
  useEffect(() => {
    if (value?.clientName) {
      setSearchQuery('');
    }
  }, [value]);

  // Valore selezionato corrente: solo se presente tra le opzioni
  const optionValues = options.map(opt => opt.value);
  let currentValue = value?.clientId ? `external-${value.clientId}` : null;
  if (currentValue && !optionValues.includes(currentValue)) {
    currentValue = null;
  }

  // Modalità creazione manuale
  if (isCreatingNew) {
    return (
      <Group>
        <TextInput
          placeholder="Nome del nuovo cliente"
          value={newClientName}
          onChange={(e) => setNewClientName(e.currentTarget.value)}
          style={{ flex: 1 }}
          error={error}
          rightSection={
            <Group gap="xs">
              <ActionIcon
                size="sm"
                color="green"
                onClick={handleCreateNew}
                disabled={!newClientName.trim()}
              >
                <IconPlus size={14} />
              </ActionIcon>
              <ActionIcon
                size="sm"
                color="red"
                onClick={() => {
                  setIsCreatingNew(false);
                  setNewClientName('');
                }}
              >
                <IconX size={14} />
              </ActionIcon>
            </Group>
          }
        />
      </Group>
    );
  }

  return (
    <Box>
      {/* Display cliente selezionato */}
      {value?.clientName && !searchQuery && (
        <Group mb="xs">
          <Badge
            leftSection={<IconBuilding size={12} />}
            color={value.clientId ? 'blue' : 'green'}
            variant="light"
            rightSection={
              <ActionIcon
                size="xs"
                color="gray"
                onClick={() => onChange({ clientId: undefined, clientName: '' })}
              >
                <IconX size={10} />
              </ActionIcon>
            }
          >
            {value.clientName}
            {!value.clientId && ' (Nuovo)'}
          </Badge>
        </Group>
      )}

      {/* Select per ricerca/selezione */}
      {(!value?.clientName || searchQuery) && (
        
        <Select
          placeholder="Cerca cliente esistente o crea nuovo..."
          searchable
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          data={searchQuery.length >= 3 ? options.map((opt) => ({ value: opt.value, label: opt.label })) : []}
          value={currentValue}
          onChange={handleSelect}
          error={error || (searchError ? 'Errore durante la ricerca' : undefined)}
          disabled={disabled}
          leftSection={<IconSearch size={16} />}
          rightSection={isSearchingExternal ? <Loader size="xs" /> : undefined}
          nothingFoundMessage={
            searchError ? (
              <Box p="sm">
                <Text size="sm" c="red" mb="xs">
                  Errore durante la ricerca
                </Text>
                <Text size="xs" c="dimmed">
                  Riprova o crea un nuovo cliente
                </Text>
              </Box>
            ) : searchQuery.length > 2 ? (
              <Box p="sm">
                <Text size="sm" c="dimmed" mb="xs">
                  Nessun cliente trovato
                </Text>
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setIsCreatingNew(true)}
                >
                  Crea nuovo cliente
                </Button>
              </Box>
            ) : (
              'Digita almeno 3 caratteri per cercare'
            )
          }
          maxDropdownHeight={300}
        />
      )}

      {/* Opzione rapida per creare nuovo */}
      {!value?.clientName && !searchQuery && (
        <Group mt="xs">
          <Button
            size="xs"
            variant="subtle"
            leftSection={<IconPlus size={14} />}
            onClick={() => setIsCreatingNew(true)}
          >
            Crea nuovo cliente
          </Button>
        </Group>
      )}

      {/* Hint */}
      {!error && (
        <Text size="xs" c="dimmed" mt="xs">
          Cerca un cliente esistente nel database o crea uno nuovo
        </Text>
      )}
    </Box>
  );
}
